import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import {
  AttendanceCustomTimeRange,
  SettingPageEmployeesSettingFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import { useDeleteCustomTimeRange } from 'modules/attendance/hooks/useDeleteCustomTimeRange'
import { translateDayInPlural } from 'modules/attendance/lib/translateDays'
import CustomTimeRangeUtils from 'modules/attendance/lib/utils/CustomTimeRangeUtils'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

interface Props {
  range: Pick<
    AttendanceCustomTimeRange,
    'id' | 'day' | 'timeSettingsCustomTimeRangeCategoryId' | 'startTime' | 'endTime' | 'poolType'
  >
  attendanceSetting: AttendanceSetting
  companyId: number
  onClose: () => void
}

const DeleteTimeBlockModal = ({ range, onClose, attendanceSetting, companyId }: Props) => {
  const [openEffectiveAtFormModal, setOpenEffectiveAtFormModal] = React.useState(false)
  const hasMajorationEnabled = hasFeature('dev_time_tracking_majoration')

  const { deleteRange, loading } = useDeleteCustomTimeRange()

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const handleDeleteRange = async (payload: {
    attendanceEmployeesSettingId: number
    ids: number[]
    companyId: number
  }) => {
    const result = await deleteRange(payload)
    const handled = handleResult(result, () => undefined)

    if (handled.success && payload.attendanceEmployeesSettingId === attendanceSetting.id) {
      app.setFlash({
        type: 'correct',
        message: hasMajorationEnabled
          ? i18n.t(
              'time_tracking.settings.custom_time_range_section.modals.delete.special_hours.success_message'
            )
          : i18n.t(
              'time_tracking.settings.custom_time_range_section.modals.delete.success_message'
            ),
      })
      onClose()
    }
  }

  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    const newSettingId = +newSetting.id
    // Fetch the custom time ranges of the newly created setting
    const newCustomTimeRanges = newSetting.customTimeRanges

    // Find the copy of the time block edited in the newly created setting
    const matchingRange = CustomTimeRangeUtils.findMatchingRange(newCustomTimeRanges, range)

    const payload = {
      attendanceEmployeesSettingId: newSettingId,
      ids: [matchingRange!.id],
      companyId: companyId,
    }

    handleDeleteRange(payload)

    const routeParams = {
      id: 'time_tracking',
      settingId: newSettingId.toString(),
      tabId: hasMajorationEnabled ? 'work_time_categories' : 'time_ranges',
    }
    navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
  }

  const onUpdate = () => {
    const payload = {
      attendanceEmployeesSettingId: attendanceSetting.id,
      ids: [range.id],
      companyId: companyId,
    }

    return handleDeleteRange(payload)
  }

  const title = hasMajorationEnabled
    ? i18n.t('time_tracking.settings.custom_time_range_section.modals.delete.special_hours.title')
    : i18n.t('time_tracking.settings.custom_time_range_section.modals.delete.specific_title')

  const description = hasMajorationEnabled
    ? i18n.t(
        'time_tracking.settings.custom_time_range_section.modals.delete.special_hours.description'
      )
    : i18n.t('time_tracking.settings.custom_time_range_section.modals.delete.message', {
        day: translateDayInPlural(range.day),
      })

  const illustration = hasMajorationEnabled ? 'alert' : 'document-edit'

  return (
    <>
      {!openEffectiveAtFormModal && (
        <Modal
          title={title}
          description={description}
          illustration={illustration}
          onClose={onClose}
          onSubmit={() => setOpenEffectiveAtFormModal(true)}
          footer={
            <>
              <Button type="negative" disabled={loading} submit>
                {i18n.t('common.delete')}
              </Button>
              <Button type="positive" disabled={loading} onClick={onClose}>
                {i18n.t('common.keep')}
              </Button>
            </>
          }
        />
      )}
      {openEffectiveAtFormModal && (
        <ManageSettingVersionModal
          onCreate={onCreateSetting}
          onUpdate={onUpdate}
          onClose={onClose}
          running={loading}
          attendanceSettingId={attendanceSetting.attributes.id}
          attendanceSettingEffectiveAt={attendanceSetting.attributes.effective_at}
        />
      )}
    </>
  )
}

export default DeleteTimeBlockModal
